import { useTranslation } from '@/hooks/use-translation';
import amplitudeService from '@/services/amplitude';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, isApiError } from '@/services/api-allo/client';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { toast } from 'sonner';
type Member = NonNullable<components['schemas']['TeamMember']>;

/** TBD:is there a case where user cannot have a team_member_id  */
export const TEAM_ADMIN_PLACEHOLDER_ID = 'team_admin_placeholder_id';

/** Max number of team members allowed on a trial — invites beyond this trigger the trial-end gate. */
export const TRIAL_TEAM_LIMIT = 5;

/** Max number of phone numbers a trial team can own — buying beyond this triggers the trial-end gate. */
export const TRIAL_NUMBER_LIMIT = 5;

export const useSubscription = () => {
  return $apiAllo.useQuery(
    'get',
    '/v2/user/subscription',
    {},
    {
      retry: false,
    },
  );
};

export const teamQueryParams = [
  'get',
  '/v1/team',
  {
    params: {
      query: {
        'filter[include_current_user]': 'true',
        'filter[active]': 'false',
      },
    },
  },
] as const;

// Team membership rarely changes mid-session. A 5-minute staleTime avoids
// refetching on every sidebar mount / window focus, which is what made the
// member avatars flicker between tab switches. All mutations that touch the
// team (invite, remove, transfer, role/name update) already invalidate
// teamQueryParams, so stale data still propagates promptly on real changes.
// `keepPreviousData` keeps the previous member list in place during background
// refetches so the section never collapses to empty mid-refresh.
const TEAM_STALE_TIME = 5 * 60 * 1000;

export const useTeam = () => {
  return $apiAllo.useQuery(...teamQueryParams, {
    staleTime: TEAM_STALE_TIME,
    retry: false,
    placeholderData: keepPreviousData,
  });
};

export const useTeamSubscriptionData = () => {
  const { data, isLoading: isTeamLoading, isPending: isTeamPending } = useTeam();
  const { data: subData, isLoading: isSubLoading, isPending: isSubPending } = useSubscription();

  const isTrial = subData?.data?.is_trial === true;
  const subscription = subData?.data?.app_role;
  const availableSeats = data?.data?.available_seats ?? 0;
  const totalSeats = (data?.data?.available_seats ?? 0) + (data?.data?.unavailable_seats ?? 0);
  const memberCount = data?.data?.members?.length ?? 0;
  const availableNumbers = data?.data?.available_numbers ?? 0;
  const usedNumbers = data?.data?.used_numbers ?? 0;
  const isStripe = subData?.data?.store === 'STRIPE';
  const isBusiness = subData?.data?.app_role === 'BUSINESS';
  const cancelsAt = subData?.data?.cancels_at ?? null;
  const isPastDue = subData?.data?.payment_status === 'FAILED';

  let base = '';
  switch (subscription) {
    case 'STARTER':
      base = 'Starter';
      break;
    case 'STARTER_PREMIUM':
      base = 'Starter_Premium';
      break;
    case 'BUSINESS':
      base = totalSeats === 0 ? 'Solo_Business' : 'Team_Business';
      break;
    default:
      base = '';
  }

  return {
    amplitudeSubType: isTrial ? `Trial_${base}` : base,
    availableSeats,
    totalSeats,
    memberCount,
    availableNumbers,
    usedNumbers,
    isBusiness,
    isTrial,
    isStripe,
    cancelsAt,
    isPastDue,
    isLoading: isTeamLoading || isSubLoading,
    isPending: isTeamPending || isSubPending,
  };
};

export const useTeamMembers = () => {
  const { data: subData, isLoading: isSubLoading } = useSubscription();
  const isBusiness = isSubLoading === false && subData?.data?.app_role === 'BUSINESS';

  const { data: userData, isLoading: isUserLoading } = $apiAllo.useQuery(
    'get',
    '/v1/user',
    {},
    { enabled: !isBusiness, retry: false },
  );
  const { data: businessData, isLoading: isBusinessLoading } = $apiAllo.useQuery(
    'get',
    '/v1/business',
    {},
    { enabled: !isBusiness, retry: false },
  );

  const query = $apiAllo.useQuery(...teamQueryParams, {
    staleTime: TEAM_STALE_TIME,
    retry: false,
    placeholderData: keepPreviousData,
    select: useCallback(
      (data: {
        data?: components['schemas']['Team'];
      }) => {
        const teamMemberId =
          userData?.data?.id && data?.data?.members
            ? getTeamMemberId(userData.data.id, data.data.members)
            : undefined;

        if (isBusiness === false || data?.data === undefined) {
          // NOTE: we simulate data here if team hasnt been created yet
          return [
            {
              id: userData?.data?.id,
              email: userData?.data?.email,
              is_active: true,
              name: userData?.data?.name,
              phone_numbers:
                businessData?.data?.phone_numbers?.map((pn) => ({
                  line_name: pn.name,
                  number: pn.number,
                  is_owner: true,
                })) ?? [],
              team_member_role: 'ADMIN',
              team_member_id: teamMemberId ?? TEAM_ADMIN_PLACEHOLDER_ID,
            },
          ] satisfies Member[];
        }

        const members = data?.data?.members ?? [];
        return members.sort((a, b) => {
          // Admin first
          if (a.team_member_role === 'ADMIN' && b.team_member_role !== 'ADMIN') {
            return -1;
          }
          if (b.team_member_role === 'ADMIN' && a.team_member_role !== 'ADMIN') {
            return 1;
          }
          // Active members sorted by created_at ascending
          if (a.is_active && b.is_active) {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return timeA - timeB;
          }
          // Inactive members sorted by created_at ascending
          if (!a.is_active && !b.is_active) {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return timeA - timeB;
          }
          // One active and one inactive: active first
          return a.is_active ? -1 : 1;
        });
      },
      [isBusiness, userData?.data, businessData?.data],
    ),
  });

  return {
    ...query,
    isLoading: query.isLoading || isSubLoading || isBusinessLoading || isUserLoading,
  };
};

function getTeamMemberId(
  userId: string,
  members: components['schemas']['TeamMember'][],
): string | undefined {
  const member = members.find((m) => {
    return m.id === userId;
  });
  return member?.team_member_id;
}

export const useTeamNumbers = () => {
  const membersQuery = useTeamMembers();

  return {
    ...membersQuery,
    data: (membersQuery?.data?.find((m) => m.team_member_role === 'ADMIN')?.phone_numbers ?? [])
      .filter((n) => n.number)
      .map((n) => ({ ...n, is_owner: undefined, is_in_call_routing: undefined })),
  };
};

// TODO: this should definitely be provided by the backend, instead of treating on frontend
export const useTeamNumbersWithMembers = () => {
  return $apiAllo.useQuery(...teamQueryParams, {
    // staleTime: 0 (vs the 5min TEAM_STALE_TIME used by useTeam / useTeamMembers)
    // is deliberate: this hook drives number-management screens where the user
    // just performed assignment changes and expects to see them reflected
    // immediately on refocus. Don't lift to TEAM_STALE_TIME without re-checking
    // those flows.
    staleTime: 0,
    retry: false,
    select: (data) => {
      const members = data?.data?.members ?? [];

      const numbers = (members.find((m) => m.team_member_role === 'ADMIN')?.phone_numbers ?? [])
        .filter((n) => n.number)
        .map((n) => ({ ...n, is_owner: undefined }));

      return numbers.map((num) => {
        const assignedMembers = members.filter((member) =>
          (member.phone_numbers ?? []).some((pn) => pn.number === num.number),
        );

        return { ...num, members: assignedMembers };
      });
    },
  });
};

/** Role value from API (MANAGER may not be in the type definition but exists in practice). */
type TeamMemberRole = 'ADMIN' | 'MANAGER' | 'MEMBER' | string | undefined;

/** Returns true if the role grants admin/manager privileges (billing, etc.). */
export const isAdminOrManagerRole = (role: TeamMemberRole): boolean =>
  role === 'ADMIN' || role === 'MANAGER';

export const useIsTeamAdmin = () => {
  const { data: userData } = $apiAllo.useQuery('get', '/v1/user', {});

  const { data: teamMembers, isLoading } = useTeamMembers();

  return {
    isTeamAdmin:
      !isLoading &&
      teamMembers?.find((m) => m.id === userData?.data?.id)?.team_member_role === 'ADMIN',
    userData,
  };
};

export const useIsAdminOrManager = () => {
  const { data: userData } = $apiAllo.useQuery('get', '/v1/user', {});
  const { data: teamMembers, isLoading } = useTeamMembers();

  if (isLoading || !userData?.data?.id || !teamMembers) {
    return { isAdminOrManager: false, isLoading: true };
  }
  const currentMember = teamMembers.find((m) => m.id === userData?.data?.id);
  return {
    isAdminOrManager: isAdminOrManagerRole(currentMember?.team_member_role),
    isLoading: false,
  };
};

export const useTeamMemberReminder = (lastReminderSentAt?: string) => {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const remindMutation = $apiAllo.useMutation('post', '/v1/members/{memberId}/remind', {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions(...teamQueryParams));
      amplitudeService.logEvent('Send Reminder Results', {
        results: 'success',
      });
      toast.success(t('team.members.invitation.send_reminder.success.text'));
    },
    onError: (error: unknown) => {
      amplitudeService.logEvent('Send Reminder Results', {
        results: 'failed',
      });
      if (isApiError(error) && error.code === 'USER_FORBIDDEN') {
        toast.error(t('team.members.invitation.send_reminder.error.text'));
      }
    },
  });

  const remindSentRecently = lastReminderSentAt
    ? dayjs(lastReminderSentAt).isAfter(dayjs().subtract(1, 'day'))
    : false;

  const sendReminder = (memberId: string) => {
    if (remindMutation.isPending) {
      return;
    }

    remindMutation.mutate({
      params: { path: { memberId } },
    });
  };

  return {
    sendReminder,
    isPending: remindMutation.isPending,
    remindSentRecently,
  };
};

export const useTransferOwnership = () => {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const transferMutation = $apiAllo.useMutation('post', '/v1/team/transfer-ownership', {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions(...teamQueryParams));
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/user', {}));
      analytics.track(ANALYTICS_EVENTS.TRANSFER_OWNERSHIP.SUCCEEDED);
      toast.success(t('team.members.invitation.transfer_ownership.success'));
    },
    onError: () => {
      analytics.track(ANALYTICS_EVENTS.TRANSFER_OWNERSHIP.FAILED);
      toast.error(t('team.members.invitation.transfer_ownership.error'));
    },
  });

  const transferOwnership = (
    newOwnerUserId: string,
    options?: { onSuccess?: () => void; onError?: () => void },
  ) => {
    if (transferMutation.isPending) {
      return;
    }

    analytics.track(ANALYTICS_EVENTS.TRANSFER_OWNERSHIP.CONFIRMED);
    transferMutation.mutate(
      { body: { new_owner_user_id: newOwnerUserId } },
      { onSuccess: options?.onSuccess, onError: options?.onError },
    );
  };

  return {
    transferOwnership,
    isPending: transferMutation.isPending,
  };
};

type RoleValue = 'MANAGER' | 'MEMBER';

export const useUpdateMemberRole = () => {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const updateRoleMutation = $apiAllo.useMutation('patch', '/v1/members/{memberId}/role', {
    onSuccess: () => {
      toast.success(t('team.members.edit.role_updated', 'Member role updated successfully'));
      queryClient.invalidateQueries($apiAllo.queryOptions(...teamQueryParams));
    },
    onError: (error) => {
      console.error('Error updating member role:', error);
      toast.error(t('team.members.edit.role_update_error', 'Failed to update member role'));
    },
  });

  const updateRole = useCallback(
    (memberId: string, role: RoleValue) => {
      if (updateRoleMutation.isPending) {
        return;
      }

      updateRoleMutation.mutate({
        params: {
          path: {
            memberId,
          },
        },
        body: {
          role,
        } as any, // Type assertion needed because schema shows requestBody?: never
      });
    },
    [updateRoleMutation],
  );

  return {
    updateRole,
    isPending: updateRoleMutation.isPending,
  };
};

export const useUpdateMemberName = () => {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const updateMemberMutation = $apiAllo.useMutation('patch', '/v1/members/{memberId}' as any, {
    onSuccess: () => {
      queryClient.invalidateQueries($apiAllo.queryOptions(...teamQueryParams));
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/user', {}));
    },
    onError: (error) => {
      console.error('Error updating member:', error);
    },
  });

  const updateName = useCallback(
    async (memberId: string, firstName: string, lastName: string) => {
      if (updateMemberMutation.isPending) {
        return;
      }

      const newName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');

      try {
        await updateMemberMutation.mutateAsync({
          params: {
            path: {
              memberId,
            },
          },
          body: {
            name: newName,
          } as any, // Type assertion needed because schema doesn't show PATCH for this endpoint
        });

        toast.success(t('team.members.edit.name_updated', 'Member name updated successfully'));
      } catch (error) {
        console.error('Error updating member name:', error);
        toast.error(t('team.members.edit.update_error', 'Failed to update member information'));
        throw error;
      }
    },
    [updateMemberMutation, t],
  );

  return {
    updateName,
    isPending: updateMemberMutation.isPending,
  };
};
